import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { saveAs } from 'file-saver';
import { columnDefs } from './column-descriptors';
import Button from '@components-clean/core/button';
import { FileUploadDialog } from '@components-clean/core/file-upload-dialog/file-upload-dialog';
import Grid from '@components-clean/core/grid-fe';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { IUser } from '@contexts/user-context';
import { useLocalState } from '@hooks/index';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import Settings from '@settings/settings';
import { GET_INTEGRATION_CONFIGS } from '@queries/central-monitoring';
import {
    getIntegrationConfigs_OpraQueries,
    getIntegrationConfigs_OpraQueries_integrationConfigs_IntegrationConfigMetadata,
} from '@queries/generated/central-monitoring.generated';

type DataItem = getIntegrationConfigs_OpraQueries_integrationConfigs_IntegrationConfigMetadata;

type Props = {
    user?: IUser;
};

export const IntegrationHistory = ({ user }: Props) => {
    const { t } = useTranslation();

    const [showUploadDialog, setShowUploadDialog] = React.useState(false);
    const { state } = useLocalState();
    const { data, loading, refetch } = useQuery<getIntegrationConfigs_OpraQueries>(GET_INTEGRATION_CONFIGS, {});

    const configs = useMemo(
        () =>
            data?.integrationConfigs
                ?.map((config) => ({
                    ...config,
                    uploadedByFullName: `${config?.createdBy?.firstName} ${config?.createdBy?.lastName}`,
                }))
                .sort(
                    (a, b) =>
                        new Date(b.uploadedDate as string).getTime() - new Date(a.uploadedDate as string).getTime(),
                ) ?? [],
        [data],
    );

    const downloadFile = async (id: string) => {
        const response = await fetch(`${Settings.api.url()}/api/integrationsconfig/download/${id}`, {
            headers: {
                Authorization: `Bearer ${user?.token}`,
                APIKey: state?.selectedOrganisation?.apiKey ?? '',
            },
        });

        if (!response.ok) throw new Error('Download failed');

        const contentDisposition = response.headers.get('Content-Disposition');
        const fileName = contentDisposition?.split('filename=')[1].split(';')[0]?.replace(/"/g, '') ?? 'download.json';

        const blob = await response.blob();
        saveAs(blob, fileName);
    };

    // eslint-disable-next-line max-len
    const exportFileName = `CM Integration Configuration History_${state?.selectedOrganisation?.name}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`;
    const canEdit = user?.canWriteSystemAdminLevel?.() ?? false;
    const columns = columnDefs(t);
    return (
        <div className="k-align-items-end k-h-full">
            <div className="k-h-full">
                <Grid
                    className="k-h-full"
                    columns={columns}
                    title={t('admin:manageCMIntegration.history.title')}
                    testDataId="cm-integration-history"
                    maxHeight="auto"
                    bespokeHeaderActions={
                        <Button
                            data-testid="upload-btn"
                            primary
                            className="k-align-self-center k-ml-2.5 k-mb-1"
                            onClick={() => setShowUploadDialog(true)}
                            disabled={!canEdit}
                        >
                            {t('admin:manageCMIntegration.upload.button')}
                        </Button>
                    }
                    showAddIcon={false}
                    loading={loading}
                    data={configs}
                    area={AuditApplicationArea.ORGANISATION}
                    service={AuditServiceType.CENTRAL_MONITORING_2}
                    dataType={AuditDataType.EXCEL_EXPORT}
                    exportFilename={exportFileName}
                    auditDescriptionPrefix="Org Admin - CM Integration"
                    rowMenuActions={[
                        {
                            label: t('admin:manageCMIntegration.history.download'),
                            id: 0,
                            icon: ['far', 'download'],
                            onClick: (dataItem) => {
                                downloadFile((dataItem as DataItem).id).catch((e) => window.console.error(e));
                            },
                        },
                    ]}
                    gridSettingsMenuDisabled
                />
            </div>
            {showUploadDialog && (
                <FileUploadDialog
                    title={t('admin:manageCMIntegration.upload.dialogTitle')}
                    saveUrl={`${Settings.api.url()}/api/integrationsconfig/upload`}
                    restrictions={{ allowedExtensions: ['.json'] }}
                    onClose={async (filesUploaded) => {
                        if (filesUploaded) await refetch();
                        setShowUploadDialog(false);
                    }}
                    label={t('admin:manageCMIntegration.upload.label')}
                    multiple={false}
                    customHint={t('admin:manageCMIntegration.upload.dropZoneHint')}
                />
            )}
        </div>
    );
};
